import { isValidEmail } from './email.js';
import { DomainError } from './errors.js';
import { compareLocalDate, type LocalDate, parseLocalDate } from './local-date.js';

/**
 * Personel kartı (Crew Profile; ADR 0131 §2): kişisel bilgi `users`'tan ayrı `crew_profiles`
 * tablosundadır. Kartı ofis yazar; gemiye yalnız Crew ID, uyruk, doğum tarihi ve yeri, cinsiyet
 * iner (`SHIP_COLUMN_ALLOWLIST`; KVKK md. 4). Saf kurallar: I/O yok, gün parametreyle gelir.
 */

/** Cinsiyet: ICAO 9303 (M, F, X). */
export const GENDERS = ['M', 'F', 'X'] as const;
export type Gender = (typeof GENDERS)[number];

/** Medeni hal (tasarım 3 "Marital Status"). */
export const MARITAL_STATUSES = ['single', 'married', 'other'] as const;
export type MaritalStatus = (typeof MARITAL_STATUSES)[number];

/** ISO 3166-1 alpha-2 resmî kodları (249). Uyruk bunlardan biridir. */
export const COUNTRY_CODES: readonly string[] = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ ' +
  'BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM ' +
  'DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS ' +
  'GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN ' +
  'KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ ' +
  'MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM ' +
  'PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV ' +
  'SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI ' +
  'VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

const COUNTRIES: ReadonlySet<string> = new Set(COUNTRY_CODES);

export function isNationalityCode(value: string): boolean {
  return COUNTRIES.has(value);
}

/** Uyruğun dile göre adı (`Intl.DisplayNames`); bilinmeyen kod olduğu gibi, boşsa boş metin. */
export function nationalityName(code: string | null | undefined, locale: string): string {
  if (!code) return '';
  try {
    return new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Kartın alanları; hepsi boş olabilir. */
export interface CrewProfileFields {
  crewCode: string | null;
  nationality: string | null;
  dateOfBirth: LocalDate | null;
  placeOfBirth: string | null;
  gender: Gender | null;
  maritalStatus: MaritalStatus | null;
  phone: string | null;
  contactEmail: string | null;
  address: string | null;
  emergencyName: string | null;
  emergencyRelation: string | null;
  emergencyPhone: string | null;
  /**
   * Kıyafet ve fiziksel özellikler (sahip 2026-11-04 B2; ADR 0158): tulum, üst ve alt beden,
   * ayakkabı numarası, boy (cm), kilo (kg), göz ve saç rengi, ayırt edici işaretler. Yalnız
   * bulutta; gemiye inmez (`SHIP_COLUMN_ALLOWLIST` dışında). Kan grubu sağlık verisidir, burada
   * değil `crew_medical_profiles`'tadır [KVKK md. 6].
   */
  coverallSize: string | null;
  upperSize: string | null;
  lowerSize: string | null;
  shoeSize: string | null;
  heightCm: string | null;
  weightKg: string | null;
  eyeColor: string | null;
  hairColor: string | null;
  distinguishingMarks: string | null;
}

export type CrewProfileInput = { [K in keyof CrewProfileFields]?: string | null | undefined };

/** Kartın alan adları (gövde ve görünüm sırası). */
export const CREW_PROFILE_FIELDS = [
  'crewCode',
  'nationality',
  'dateOfBirth',
  'placeOfBirth',
  'gender',
  'maritalStatus',
  'phone',
  'contactEmail',
  'address',
  'emergencyName',
  'emergencyRelation',
  'emergencyPhone',
  'coverallSize',
  'upperSize',
  'lowerSize',
  'shoeSize',
  'heightCm',
  'weightKg',
  'eyeColor',
  'hairColor',
  'distinguishingMarks',
] as const satisfies readonly (keyof CrewProfileFields)[];

type TextField = Exclude<keyof CrewProfileFields, 'gender' | 'maritalStatus'>;

/** Metin alanlarının en büyük uzunluğu. */
const MAX: Record<TextField, number> = {
  crewCode: 20,
  nationality: 2,
  dateOfBirth: 10,
  placeOfBirth: 120,
  phone: 40,
  contactEmail: 200,
  address: 300,
  emergencyName: 200,
  emergencyRelation: 60,
  emergencyPhone: 40,
  coverallSize: 12,
  upperSize: 12,
  lowerSize: 12,
  shoeSize: 12,
  heightCm: 3,
  weightKg: 5,
  eyeColor: 40,
  hairColor: 40,
  distinguishingMarks: 300,
};

/** Boy: 50–250 cm, tam sayı. */
const HEIGHT = /^\d{2,3}$/;
/** Kilo: 20–300 kg, en çok bir ondalık (virgül de kabul edilir, noktaya çevrilir). */
const WEIGHT = /^\d{2,3}(\.\d)?$/;

function measure(
  input: CrewProfileInput,
  field: 'heightCm' | 'weightKg',
  pattern: RegExp,
  min: number,
  max: number,
): string | null {
  const raw: unknown = input[field];
  if (raw === undefined || raw === null) return null;
  if (typeof raw !== 'string') throw new DomainError('VALIDATION_FAILED', field, field);
  const value = raw.trim().replace(',', '.');
  if (value === '') return null;
  const number = Number(value);
  if (!pattern.test(value) || number < min || number > max) {
    throw new DomainError('VALIDATION_FAILED', field, field);
  }
  return value;
}

function text(input: CrewProfileInput, field: TextField): string | null {
  const raw: unknown = input[field];
  if (raw === undefined || raw === null) return null;
  if (typeof raw !== 'string') throw new DomainError('VALIDATION_FAILED', field, field);
  const value = raw.trim().replace(/\s+/g, ' ');
  if (value === '') return null;
  // Uyruk kodu ayrı denetlenir (`INVALID_NATIONALITY`)
  if (field !== 'nationality' && value.length > MAX[field]) {
    throw new DomainError('VALIDATION_FAILED', field, field);
  }
  return value;
}

function oneOf<T extends string>(
  input: CrewProfileInput,
  field: 'gender' | 'maritalStatus',
  values: readonly T[],
): T | null {
  const raw: unknown = input[field];
  if (raw === undefined || raw === null || raw === '') return null;
  if (typeof raw !== 'string' || !(values as readonly string[]).includes(raw)) {
    throw new DomainError('VALIDATION_FAILED', field, field);
  }
  return raw as T;
}

/**
 * Kart girdisini doğrular ve biçimler: kırpma, uzunluk (`VALIDATION_FAILED`, ayrıntı alan adı),
 * uyruk ISO alpha-2 (`INVALID_NATIONALITY`), doğum tarihi gemi günü biçiminde ve gelecekte değil
 * (`INVALID_LOCAL_DATE`, `FUTURE_DATE`), iletişim e-postası biçimli (`INVALID_EMAIL`).
 */
export function parseCrewProfile(input: CrewProfileInput, today: LocalDate): CrewProfileFields {
  const nationality = text(input, 'nationality')?.toUpperCase() ?? null;
  if (nationality !== null && !isNationalityCode(nationality)) {
    throw new DomainError('INVALID_NATIONALITY', nationality, 'nationality');
  }
  const dateOfBirth = text(input, 'dateOfBirth');
  if (dateOfBirth !== null) {
    parseLocalDate(dateOfBirth);
    if (compareLocalDate(dateOfBirth, today) > 0) {
      throw new DomainError('FUTURE_DATE', 'dateOfBirth', 'dateOfBirth');
    }
  }
  const contactEmail = text(input, 'contactEmail');
  if (contactEmail !== null && !isValidEmail(contactEmail)) {
    throw new DomainError('INVALID_EMAIL', 'contactEmail', 'contactEmail');
  }
  return {
    crewCode: text(input, 'crewCode')?.toUpperCase() ?? null,
    nationality,
    dateOfBirth,
    placeOfBirth: text(input, 'placeOfBirth'),
    gender: oneOf(input, 'gender', GENDERS),
    maritalStatus: oneOf(input, 'maritalStatus', MARITAL_STATUSES),
    phone: text(input, 'phone'),
    contactEmail,
    address: text(input, 'address'),
    emergencyName: text(input, 'emergencyName'),
    emergencyRelation: text(input, 'emergencyRelation'),
    emergencyPhone: text(input, 'emergencyPhone'),
    coverallSize: text(input, 'coverallSize')?.toUpperCase() ?? null,
    upperSize: text(input, 'upperSize')?.toUpperCase() ?? null,
    lowerSize: text(input, 'lowerSize')?.toUpperCase() ?? null,
    shoeSize: text(input, 'shoeSize')?.toUpperCase() ?? null,
    heightCm: measure(input, 'heightCm', HEIGHT, 50, 250),
    weightKg: measure(input, 'weightKg', WEIGHT, 20, 300),
    eyeColor: text(input, 'eyeColor'),
    hairColor: text(input, 'hairColor'),
    distinguishingMarks: text(input, 'distinguishingMarks'),
  };
}

const CREW_CODE = /^C-(\d{3,})$/;

/** Kendiliğinden verilen Crew ID biçimi: `C-` + en az üç hane. */
export function isCrewCode(value: string): boolean {
  return CREW_CODE.test(value);
}

/** Sıradaki Crew ID: `C-NNN` biçimindekilerin en büyüğü + 1 (en az üç hane); başka biçim sayılmaz. */
export function nextCrewCode(existing: readonly string[]): string {
  let max = 0;
  for (const value of existing) {
    const match = CREW_CODE.exec(value);
    if (match) max = Math.max(max, Number(match[1]));
  }
  return `C-${String(max + 1).padStart(3, '0')}`;
}
